import { useGetSubscriptionsPlans } from "@birdwatch/client";
import { useMemo } from "react";

/** A plan's catalog price — the recurring amount once the market launches. */
export type PresaleStandardPrice = {
  /** The platform's own label, so the card formats no money of its own. */
  formattedPrice: string | null;
  /** `"month"` / `"year"`. */
  interval: string | null;
};

/**
 * The STANDARD recurring price of the plan being reserved.
 *
 * Deliberately omits `checkoutLinkId`. Passing it makes the platform apply the
 * campaign link's price override (`applyCheckoutLinkPlanPriceOverride` in
 * `platform/src/resources/subscriptions/plans-list.repository.ts`), which returns the
 * $0/mo presale rate — the very figure this hook exists to contrast with. That is also
 * why `usePresaleMembership` (which reads `/checkout-links/:id/plan`) cannot answer
 * this: that endpoint always applies the override.
 *
 * `GET /subscriptions/plans` is public, so this works on every step of the funnel.
 */
export function usePresaleStandardPrice(planKey: string | undefined): {
  data: PresaleStandardPrice | null;
  isLoading: boolean;
} {
  const { data, isLoading } = useGetSubscriptionsPlans()
    .query({})
    .get({ enabled: !!planKey });

  // Memoized for the same reason `usePresaleMembership` memoizes: callers put the
  // result in dependency arrays, and a fresh literal per render defeats them.
  const price = useMemo(() => {
    const plan = data?.plans?.find((item) => item.planKey === planKey);
    if (!plan?.price) return null;
    return {
      formattedPrice: plan.price.formattedPrice ?? null,
      interval: plan.price.interval ?? null,
    };
  }, [data, planKey]);

  return {
    data: price,
    // A disabled query must never report loading, or the caller's `&&` gate stops
    // being the only one (same guard as `usePresaleMembership`).
    isLoading: !!planKey && isLoading,
  };
}
